import type { ChangeEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { PopperPlacementType } from '@mui/base/Popper/Popper.types';
import {
  Alert,
  Box,
  ClickAwayListener,
  Divider,
  Grow,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from '@mui/material';

import type { OwnershipOption } from 'api/data/ownership/models/OwnershipOption';
import { DEBOUNCE_TIME } from 'app/AppConstants';
import { OwnershipAvatar } from 'components/data-display/avatar/OwnershipAvatar';
import { InputSearchField } from 'components/inputs/search/InputSearchField';
import { useDebounce } from 'hooks/use-debounce';
import { useSelectedProjectOwnershipOptions } from 'state/ownership/ownershipMappingHooks';
import type { Nullable } from 'utils/values';

interface OwnershipSelectorProps {
  onChange: (option?: OwnershipOption) => void;

  templateNodeId: string;
  propertyId: string;

  anchorEl?: HTMLElement | null;

  placement?: PopperPlacementType;
  transformOrigin?: string;

  hideRoles?: boolean;

  value: Nullable<string>;

  onClose: () => void;
}

export function OwnershipSelector({
  onChange,
  templateNodeId,
  propertyId,
  anchorEl,
  placement,
  transformOrigin,
  hideRoles,
  value,
  onClose
}: OwnershipSelectorProps) {
  const theme = useTheme();

  const {
    options: availableOptions,
    isLoading: isLoadingOptions,
    isError: isOwnershipOptionsError
  } = useSelectedProjectOwnershipOptions(templateNodeId, propertyId);

  const [filter, setFilter] = useState<string>('');
  const [selected, setSelected] = useState<OwnershipOption | undefined>(
    availableOptions.find(({ userName }) => userName === value)
  );

  useEffect(() => {
    setSelected(availableOptions.find(({ userName }) => userName === value));
  }, [availableOptions, value]);

  const [isNavigating, setIsNavigating] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);

  const debouncedFilter = useDebounce(filter, DEBOUNCE_TIME);

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const onMenuClose = useCallback(() => {
    setFilter('');
    setIsNavigating(false);
    onClose();
  }, [onClose]);

  const onMenuKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (event.key === 'Escape' || event.key === 'Tab') {
        event.preventDefault();
        event.stopPropagation();

        onMenuClose();
      }
    },
    [onMenuClose]
  );

  const onMenuScroll = (event: any) => {
    setIsScrolling((event.target?.scrollTop ?? 0) > 0);
  };

  const onSearchInputApply = (value: string) => {
    setFilter(value);

    if (searchInputRef.current !== null) {
      searchInputRef.current.focus();
    }
  };

  const onSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFilter(event.target.value);

    if (searchInputRef.current !== null) {
      searchInputRef.current.focus();
    }
  };

  const onSearchInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        onMenuClose();
      }

      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        event.stopPropagation();
      }

      if (event.key === 'Tab' || (event.shiftKey && event.key === 'Tab')) {
        event.preventDefault();
      }

      setIsNavigating(event.key === 'ArrowUp' || event.key === 'ArrowDown');
    },
    [onMenuClose]
  );

  const onSelectionChange = useCallback(
    (event: Event, option?: OwnershipOption) => {
      setFilter('');
      setSelected(option);

      if (typeof onChange === 'function') {
        onChange(option);
      }

      onMenuClose();
    },
    [onChange, onMenuClose]
  );

  useEffect(() => {
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, []);

  const searchTerm = debouncedFilter.toLocaleLowerCase();
  const filteredOptions = useMemo(() => {
    const options = !searchTerm
      ? availableOptions
      : availableOptions.filter(({ title, userName, roles }) => {
          return (
            title.toLocaleLowerCase().includes(searchTerm) ||
            userName.toLocaleLowerCase().includes(searchTerm) ||
            roles.some((role) => role.toLocaleLowerCase().includes(searchTerm))
          );
        });

    return options.sort((a, b) => a.title.localeCompare(b.title));
  }, [availableOptions, searchTerm]);

  const filteredRoles = useMemo(() => {
    if (hideRoles) {
      return [];
    }

    return filteredOptions
      .flatMap((o) => o.roles)
      .filter((value, index, array) => value !== 'None' && array.indexOf(value) === index)
      .filter((role) => role.toLocaleLowerCase().includes(searchTerm))
      .sort((a, b) => a.localeCompare(b));
  }, [filteredOptions, hideRoles, searchTerm]);

  return (
    <>
      {anchorEl && (
        <ClickAwayListener onClickAway={onMenuClose}>
          <Popper
            anchorEl={anchorEl}
            placement={placement ?? 'bottom-end'}
            role={undefined}
            open
            sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
            transition
          >
            {({ TransitionProps }) => (
              <Grow {...TransitionProps} style={{ transformOrigin: transformOrigin ?? 'right top' }}>
                <Paper
                  elevation={3}
                  sx={{
                    backgroundColor: (theme) => theme.palette.common.white,
                    padding: theme.spacing(1, 0),
                    width: '256px'
                  }}
                >
                  <Stack spacing={0.5}>
                    <Box
                      sx={{
                        transition: 'box-shadow 0.25s ease-in-out',
                        boxShadow: isScrolling ? theme.shadows[2] : theme.shadows[0],
                        padding: theme.spacing(1, 2)
                      }}
                      autoFocus
                    >
                      <InputSearchField
                        onChange={onSearchInputChange}
                        onKeyDown={onSearchInputKeyDown}
                        elevation={2}
                        inputRef={searchInputRef}
                        value={filter}
                        autoFocus={!isNavigating}
                      />
                    </Box>
                    <MenuList
                      onKeyDown={onMenuKeyDown}
                      onScroll={onMenuScroll}
                      autoFocusItem={isNavigating}
                      sx={{
                        overflowY: 'auto',
                        maxHeight: 300
                      }}
                      disablePadding
                    >
                      {isLoadingOptions ? (
                        // Mirrors the eventual avatar + name/role MenuItem shape (canonical
                        // "skeleton reflects the content" loading pattern) rather than a bare
                        // spinner — and, critically, keeps this distinct from the genuinely-empty
                        // state below so a slow roster fetch doesn't read as "no eligible owners".
                        <Stack spacing={0} sx={{ px: 2, py: 1 }} data-testid='ownership-selector-loading'>
                          {[0, 1].map((key) => (
                            <Stack key={key} direction='row' spacing={1.5} alignItems='center' sx={{ py: 0.75 }}>
                              <Skeleton variant='circular' width={32} height={32} />
                              <Stack spacing={0.5} flex={1}>
                                <Skeleton variant='text' width='60%' />
                                <Skeleton variant='text' width='40%' />
                              </Stack>
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        <>
                          {/* Unassigned stays offered even on a fetch error — it needs no roster data,
                              so a failed lookup shouldn't block the one option that's always safe. */}
                          {debouncedFilter.length === 0 && (
                            <MenuItem
                              key={`${templateNodeId}-option-unassigned`}
                              onClick={(event) => onSelectionChange(event.nativeEvent, undefined)}
                              selected={selected?.userName === ''}
                            >
                              <ListItemAvatar>
                                <OwnershipAvatar userName='' color={theme.palette.grey['A700']} size='medium' />
                              </ListItemAvatar>

                              <ListItemText
                                primary='Unassigned'
                                secondary='None'
                                sx={{ padding: theme.spacing(0.25, 0) }}
                              />
                            </MenuItem>
                          )}

                          {isOwnershipOptionsError ? (
                            // Distinguished from the genuinely-empty case below: a failed fetch must
                            // never read as "confirmed no eligible owners". Deliberately inert — both
                            // queries behind this list have `retryOnMount: false` (an errored, no-data
                            // query would otherwise refetch on every new subscriber, and this popper
                            // mounts one per edited cell), so a refresh really is the way out and the
                            // copy says so rather than offering an action that can't deliver.
                            <Alert
                              component='li'
                              severity='error'
                              variant='standard'
                              sx={{ m: 1 }}
                              data-testid='ownership-selector-error'
                            >
                              Could not load owners. Refresh the page to try again.
                            </Alert>
                          ) : (
                            <>
                              {!hideRoles && filteredRoles.length > 0 && (
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                  component='li'
                                  sx={{ padding: theme.spacing(0.875, 2) }}
                                >
                                  Roles
                                </Typography>
                              )}

                              {!hideRoles &&
                                filteredRoles.length > 0 &&
                                filteredRoles.map((role) => (
                                  <MenuItem
                                    key={`${templateNodeId}-role-${role}`}
                                    onClick={() => onSearchInputApply(role)}
                                    tabIndex={0}
                                  >
                                    <Typography noWrap>{role}</Typography>
                                  </MenuItem>
                                ))}

                              {!hideRoles && filteredRoles.length > 0 && <Divider />}

                              {!hideRoles && filteredOptions.length > 0 && (
                                <Typography
                                  variant='body2'
                                  color='text.secondary'
                                  component='li'
                                  sx={{ padding: theme.spacing(0.875, 2) }}
                                >
                                  Users
                                </Typography>
                              )}

                              {filteredOptions.length > 0 &&
                                filteredOptions.map((option) => (
                                  <MenuItem
                                    key={`${templateNodeId}-option-${option.id}`}
                                    onClick={(event) => onSelectionChange(event.nativeEvent, option)}
                                    selected={selected?.userName === option.userName}
                                  >
                                    <ListItemAvatar>
                                      <OwnershipAvatar
                                        userName={option.userName}
                                        color={theme.palette.grey['A700']}
                                        size='medium'
                                      />
                                    </ListItemAvatar>

                                    <ListItemText
                                      primary={option.title}
                                      secondary={option.roles.join(', ')}
                                      sx={{ padding: theme.spacing(0.25, 0) }}
                                    />
                                  </MenuItem>
                                ))}

                              {debouncedFilter.length > 0 && filteredOptions.length === 0 && (
                                <MenuItem disabled>No options</MenuItem>
                              )}

                              {debouncedFilter.length === 0 && filteredOptions.length === 0 && (
                                <MenuItem disabled sx={{ whiteSpace: 'normal' }}>
                                  No eligible owners for this property.
                                </MenuItem>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </MenuList>
                  </Stack>
                </Paper>
              </Grow>
            )}
          </Popper>
        </ClickAwayListener>
      )}
    </>
  );
}
